import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import Shipments from "./Shipments";
import FTL from "./ftl/FTL";
import LTL from "./ltl/LTL";
import ProcessingMessage from "./ProcessingMessage";
import ShipmentDashboard from "./ShipmentDashboard";
import { LTLShipment, FTLShipment } from "@src/types/api";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";
import { createShipmentActions } from "@src/load-agent/app/TaskpaneServicesContext";
import ProviderSetupInfoBubble from "./ProviderSetupInfoBubble";
import { emailShipmentsFtl, emailShipmentsLtl } from "@taskpane/utils/emailShipments";
import { useEmailData } from "@taskpane/hooks/useEmailData";
import { useSupportMode } from "@taskpane/hooks/useSupportMode";
import { strings } from "@src/i18n/strings";
import AppTopBar, { type AppView } from "./AppTopBar";
import ConfirmDialog from "./ConfirmDialog";
import ErrorScreen from "./ErrorScreen";
import WidePaneTip from "./WidePaneTip";

type Route = "Shipments" | "LTL" | "FTL";
const appStrings = strings.components.app;

export const LAST_VIEW_STORAGE_KEY = "loadAgent.lastView.v1";

const SHOW_DIAGNOSTICS_BY_DEFAULT = process.env.NODE_ENV !== "production";

function readLastView(): AppView {
  try {
    return window.localStorage.getItem(LAST_VIEW_STORAGE_KEY) === "dashboard"
      ? "dashboard"
      : "email";
  } catch {
    return "email";
  }
}

function writeLastView(view: AppView): void {
  try {
    window.localStorage.setItem(LAST_VIEW_STORAGE_KEY, view);
  } catch {
    // Best effort persistence only.
  }
}

export default function App() {
  const {
    emailData,
    setEmailData,
    refreshEmailData,
    retryFetch,
    resetAndReloadForCurrentItem,
    loading,
    error,
    isProcessing,
  } = useEmailData();
  const ports = useTaskpaneServices();
  const supportMode = useSupportMode();
  const [view, setView] = useState<AppView>(readLastView);
  const [route, setRoute] = useState<Route>(appStrings.route.shipments);
  const [clickedLTLShipment, setClickedLTLShipment] = useState<LTLShipment | null>(null);
  const [clickedFTLShipment, setClickedFTLShipment] = useState<FTLShipment | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmRefreshOpen, setConfirmRefreshOpen] = useState(false);
  const [dashboardRefreshToken, setDashboardRefreshToken] = useState(0);
  const [formResetKey, setFormResetKey] = useState(0);
  const isDashboard = view === "dashboard";
  const changeView = useCallback((next: AppView) => {
    setView(next);
    writeLastView(next);
  }, []);
  const goLTL = useCallback((shipment: LTLShipment | null) => {
    setClickedLTLShipment(shipment);
    setRoute(appStrings.route.ltl);
  }, []);
  const goFTL = useCallback((shipment: FTLShipment | null) => {
    setClickedFTLShipment(shipment);
    setRoute(appStrings.route.ftl);
  }, []);

  React.useEffect(() => {
    const subscribe = ports.office.onItemChanged;

    if (typeof subscribe !== "function") return;
    const unsubscribe = subscribe(() => {
      // Reset the single-email view for the new item, but leave nav mode as-is: the org-wide
      // dashboard refreshes itself on item change, so we don't force the user back to it.
      setRoute(appStrings.route.shipments);
      setClickedLTLShipment(null);
      setClickedFTLShipment(null);
      resetAndReloadForCurrentItem();
    });

    return () => {
      unsubscribe?.();
    };
  }, [ports.office, resetAndReloadForCurrentItem]);
  const shipmentActions = useMemo(() => {
    if (emailData) {
      return createShipmentActions(
        emailData,
        { loadApp: ports.loadApp },
        undefined,
        (mode: "LTL" | "FTL", shipmentId: string) => {
          if (mode === appStrings.route.ltl) {
            const ltlShipment = emailShipmentsLtl(emailData).find(
              (s) => s.shipmentId === shipmentId
            );

            if (ltlShipment) {
              setClickedLTLShipment(ltlShipment);
              setRoute(appStrings.route.ltl);
            }
          } else if (mode === appStrings.route.ftl) {
            const ftlShipment = emailShipmentsFtl(emailData).find(
              (s) => s.shipmentId === shipmentId
            );

            if (ftlShipment) {
              setClickedFTLShipment(ftlShipment);
              setRoute(appStrings.route.ftl);
            }
          }
        },
        refreshEmailData
      );
    }
    return ports.shipmentActions;
  }, [emailData, ports.loadApp, ports.shipmentActions, refreshEmailData]);
  const shipmentsScreenKey = useMemo(
    () =>
      `${emailData?.internetMessageId ?? appStrings.placeholder.noEmail}-${emailData ? appStrings.placeholder.session : appStrings.placeholder.placeholder}`,
    [emailData]
  );
  const verifiedSender = useMemo(() => {
    const exactMatch = emailData?.organization?.senderExactCustomerMatch;

    if (exactMatch === true) return true;
    const domainMatch = emailData?.organization?.senderCustomerVerified;

    if (domainMatch === true || domainMatch === false) return domainMatch;
    return undefined;
  }, [
    emailData?.organization?.senderExactCustomerMatch,
    emailData?.organization?.senderCustomerVerified,
    emailData?.internetMessageId,
  ]);
  const setActiveComponent = useCallback((name: string) => {
    if (name === appStrings.route.shipments) return setRoute(appStrings.route.shipments);
  }, []);
  const isFormRoute = route === appStrings.route.ltl || route === appStrings.route.ftl;
  const runEmailRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const fresh = await refreshEmailData();

      if (!fresh) return;
      if (route === appStrings.route.ltl && clickedLTLShipment?.shipmentId) {
        const match = emailShipmentsLtl(fresh).find(
          (s) => s.shipmentId === clickedLTLShipment.shipmentId
        );

        if (match) setClickedLTLShipment(match);
      } else if (route === appStrings.route.ftl && clickedFTLShipment?.shipmentId) {
        const match = emailShipmentsFtl(fresh).find(
          (s) => s.shipmentId === clickedFTLShipment.shipmentId
        );

        if (match) setClickedFTLShipment(match);
      }
      if (isFormRoute) setFormResetKey((k) => k + 1);
    } finally {
      setRefreshing(false);
    }
  }, [refreshEmailData, route, clickedLTLShipment, clickedFTLShipment, isFormRoute]);
  const handleRefresh = useCallback(() => {
    if (isDashboard) {
      setDashboardRefreshToken((t) => t + 1);
      return;
    }
    if (error || isProcessing || !emailData) {
      retryFetch();
      return;
    }
    if (isFormRoute) {
      setConfirmRefreshOpen(true);
      return;
    }
    void runEmailRefresh();
  }, [isDashboard, error, isProcessing, emailData, retryFetch, isFormRoute, runEmailRefresh]);
  const confirmRefresh = useCallback(() => {
    setConfirmRefreshOpen(false);
    void runEmailRefresh();
  }, [runEmailRefresh]);
  const cancelRefresh = useCallback(() => setConfirmRefreshOpen(false), []);
  const content = useMemo(() => {
    if (loading && !error && !isProcessing) {
      return (
        <div className="loading-container">
          <Spinner size={SpinnerSize.large} label={appStrings.loadingLabel} />
        </div>
      );
    }
    if (isProcessing) {
      return <ProcessingMessage onRefresh={retryFetch} />;
    }
    if (error) {
      return <ErrorScreen error={error} onRetry={retryFetch} />;
    }
    switch (route) {
      case appStrings.route.shipments:
        return (
          <Shipments
            key={shipmentsScreenKey}
            setActiveComponent={setActiveComponent}
            emailData={emailData}
            verifiedSender={verifiedSender}
            refreshEmailData={refreshEmailData}
            goLTL={goLTL}
            goFTL={goFTL}
            shipmentActions={shipmentActions}
          />
        );
      case appStrings.route.ltl:
        return (
          <LTL
            key={`ltl-${formResetKey}`}
            setActiveComponent={setActiveComponent}
            emailData={emailData}
            setEmailData={setEmailData}
            clickedLTLShipment={clickedLTLShipment}
            setClickedLTLShipment={setClickedLTLShipment}
            selectedMode="LTL"
            setSelectedMode={(m: "LTL" | "FTL" | null) => {
              if (m === null) setRoute(appStrings.route.shipments);
            }}
          />
        );
      case appStrings.route.ftl:
        return (
          <FTL
            key={`ftl-${formResetKey}`}
            setActiveComponent={setActiveComponent}
            emailData={emailData}
            setEmailData={setEmailData}
            clickedFTLShipment={clickedFTLShipment}
            setClickedFTLShipment={setClickedFTLShipment}
            selectedMode="FTL"
            setSelectedMode={(m: "LTL" | "FTL" | null) => {
              if (m === null) setRoute(appStrings.route.shipments);
            }}
          />
        );
      default:
        return null;
    }
  }, [
    loading,
    error,
    isProcessing,
    retryFetch,
    route,
    emailData,
    verifiedSender,
    shipmentsScreenKey,
    setEmailData,
    clickedLTLShipment,
    clickedFTLShipment,
    setActiveComponent,
    refreshEmailData,
    shipmentActions,
    goLTL,
    goFTL,
    formResetKey,
  ]);

  return (
    <>
      <AppTopBar
        view={view}
        onViewChange={changeView}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
      <WidePaneTip />
      {isDashboard ? (
        <ShipmentDashboard refreshToken={dashboardRefreshToken} />
      ) : (
        <div>{content}</div>
      )}
      <ConfirmDialog
        open={confirmRefreshOpen}
        title={appStrings.refreshConfirm.title}
        message={appStrings.refreshConfirm.message}
        confirmText={appStrings.refresh}
        onConfirm={confirmRefresh}
        onCancel={cancelRefresh}
      />
      {!isDashboard && (SHOW_DIAGNOSTICS_BY_DEFAULT || supportMode) && (
        <ProviderSetupInfoBubble
          providerSetup={emailData?.providerSetup}
          selectedMode={
            route === appStrings.route.ltl
              ? appStrings.route.ltl
              : route === appStrings.route.ftl
                ? appStrings.route.ftl
                : null
          }
        />
      )}
    </>
  );
}
